import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FileSignature, FileCheck, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { businessMonthKey, businessToday } from '@/lib/format';

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))'];

export default function ContractAgreementsAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const { data: summaryData, isLoading } = useQuery({
        queryKey: ['contract-agreements-analytics', fromDate, toDate],
        queryFn: async () => {
            const { data: agreements, error } = await supabase
                .from('contract_agreements')
                .select('id, signed_at, type')
                .gte('signed_at', fromDate)
                .lte('signed_at', toDate + 'T23:59:59');
            if (error) throw error;

            const totalAgreements = agreements?.length || 0;

            // Count by type
            const byType: Record<string, number> = {};
            agreements?.forEach(a => {
                const type = a.type || 'Unknown';
                byType[type] = (byType[type] || 0) + 1;
            });
            const typeData = Object.entries(byType).map(([name, value]) => ({ name, value }));

            // Monthly trend
            const monthlyMap = new Map<string, number>();
            const current = new Date(monthRange.from.getFullYear(), monthRange.from.getMonth(), 1);
            while (current <= monthRange.to) {
                const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
                monthlyMap.set(key, 0);
                current.setMonth(current.getMonth() + 1);
            }

            agreements?.forEach(a => {
                // The stored Ghana month. `signed_at` is a timestamptz, so `new Date(…)` read
                // through local getters moves every signature within the viewer's offset of a
                // month boundary into the neighbouring bar.
                const key = businessMonthKey(a.signed_at);
                if (key && monthlyMap.has(key)) monthlyMap.set(key, (monthlyMap.get(key) || 0) + 1);
            });

            const monthlyData = Array.from(monthlyMap.entries()).map(([month, count]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
                count,
            }));

            return { totalAgreements, typeData, monthlyData };
        },
    });

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Agreements</CardTitle>
                        <FileSignature className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{summaryData?.totalAgreements || 0}</div>
                        <p className="text-xs text-muted-foreground">Signed documents</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Period</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{format(monthRange.from, 'MMM yy')} - {format(monthRange.to, 'MMM yy')}</div>
                        <p className="text-xs text-muted-foreground">Selected date range</p>
                    </CardContent>
                </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Monthly Agreements Signed</CardTitle>
                        <CardDescription>Contract agreements over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={summaryData?.monthlyData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="month" className="text-xs" />
                                <YAxis className="text-xs" />
                                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }} />
                                <Area type="monotone" dataKey="count" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.6} name="Agreements" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Agreement Types</CardTitle>
                        <CardDescription>Distribution by type</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {summaryData?.typeData && summaryData.typeData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={summaryData.typeData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                                        {summaryData.typeData.map((_, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                                    </Pie>
                                    <Legend />
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-[280px] text-muted-foreground">No data</div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
